
import {
  PrimaryButton,
  CQTreeTable,
  usePaginationSessionStorage,
} from '@laxmimanogna/code-quick-components';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { 
  Box, 
  HStack, 
  Text, 
  Tooltip, 
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import {
  CHART_SUBHEADER,
  ChartStatusBadge,
  BatchStatusIndicator,
  AttachmentCell,
} from './table_config';
import { replaceRoute } from '../../../../utils/common.utils';
import AppColors from '../../../../constants/AppColors';
import { format } from 'date-fns';
import { AuthContext } from '../../../../providers/AuthProvider';
import { createTreeTableProps, paginationProps } from '../../../../utils/cq_tree_table_props';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../../constants/Routes';
import AuditSheetFileViewer from '../../../audit_sheet/components/AuditSheetFileViewer'
import NotesDrawer from '../../../audit_sheet/components/NotesDrawer';
import { EyeAvatarIcon } from '../../../../constants/IconData';
import { MyAuditContext } from '../../../../providers/MyAuditProvider';
import { READ_ONLY_SHEET_STATUSES } from '../../../audit_sheet/utils/constant'
import { ClientType } from '../../../../utils/clientTypeUtils'

const columnHelper = createColumnHelper();

const NewAuditTable = ({
  onOpenNotes, 
  PAGE_NAME, 
  TABLE_NAME,
  filterResetTrigger,
  tableToggleData
}) => {
    const auditContext = useContext(MyAuditContext);
    const authContext = useContext(AuthContext);
    const { 
        allCharts, 
        isAllChartsLoading,
        getAllCharts, 
        handleApplyFilter,
        filterParams,
        myAuditsCheckbox
    } = auditContext;

    const {
      getPaginationSession,
      setPaginationSession
    } = usePaginationSessionStorage();

    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const navigate = useNavigate();
    const [ordering, setOrdering] = useState(getPaginationSession(PAGE_NAME, TABLE_NAME)?.ordering || '');
    const tableRef = React.useRef(null);

    useEffect(() => {
      if (tableToggleData.expand) {
        (allCharts ?? []).forEach((_, idx) => tableRef.current.expandRow(String(idx)))
      }
    }, [tableToggleData.expand]);

    useEffect(() => {
      if (tableToggleData.collapse) {
        tableRef.current.collapseAll();
      }
    }, [tableToggleData.collapse])

    useEffect(() => {
      if (filterResetTrigger === null) {
        return;
      }
      setOrdering(null);
      tableRef.current?.setCustomSorting();
    }, [filterResetTrigger])

    useEffect(() => {
      if (!ordering) {
        return;
      }
      let rev = false, key = ordering;
      if (ordering[0] === "-") {
        rev = true;
        key = ordering.substring(1);
      }

      tableRef?.current.setCustomSorting([{'id': key, desc: rev}])
    }, [])

     // keeps the batch data in session storage so that we can 
  // highlight this item from the table after returning to this page
  const onBatchNavigation = (
    batchRowId,
    batchId
  ) => {
    getAllCharts.saveNavigationState(
      PAGE_NAME,
      TABLE_NAME,
      [batchRowId, batchId]
    );
  }

  // keeps the chart data in session storage so that we can 
  // highlight this item from the table after returning to this page
  const onChartNavigation = (
    batchRowId,
    chartRowId,
    batchId,
    chartId
  ) => {
    getAllCharts.saveNavigationState(
      PAGE_NAME,
      TABLE_NAME,
      [
        batchRowId,
        chartRowId, 
        batchId,
        chartId
      ]
    );
  }

  const initializingNavState = () => {
      if ((allCharts ?? []).length > 0) {
        const itemId = getAllCharts.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
        if (itemId) {
          const navData = itemId.split(',').map(Number);
          // for viewing batch 
          if (navData.length === 2) {
            const [batchRow, batchId] = navData;
            if (allCharts?.[batchRow]?.upload_id == batchId) {
              setHighlightedRowId(batchId);
            } 
          } else {
            // for viewing chart 
            const [batchRow, chartRow , batchId, chartId] = navData;
            if (allCharts?.[batchRow]?.upload_id == batchId && allCharts?.[batchRow]?.subRows?.[chartRow].id === chartId) {
              tableRef.current.expandRow(String(batchRow));
              setHighlightedRowId(chartId);
            }
          }             
                            
          setTimeout(() => {
            setHighlightedRowId(null);
          }, 2000);
        }
      }
    }




    useEffect(() => {
        initializingNavState();
    }, [allCharts]);
    
    useEffect(() => {
      let searching = !!(filterParams.searchText && filterParams.searchText.trim());
      getAllCharts.loadAPI(PAGE_NAME, TABLE_NAME, searching);
    }, [filterParams.ordering, filterParams.chartStatusType, filterParams.triggerSearch, getAllCharts.showOrdering]);

    useEffect(() => {
      if ((ordering || filterParams.ordering) && ordering !== filterParams.ordering) {
        setPaginationSession(PAGE_NAME, TABLE_NAME, {
          ...getPaginationSession(PAGE_NAME, TABLE_NAME),
          page: 1,
          size: 10
        })
        handleApplyFilter({
          ...filterParams,
          ordering: ordering
        })
      }
    }, [ordering]);


    const columns = useMemo(() => [
    
        // ── Col 1: Batch / Chart ID ───────────────────────────────────────────────
        // subHeader value is an object → enables custom bg/color AND sortable toggle
        columnHelper.accessor('name', {
          id: 'name',
          header: () => 'Batch',
          subHeader: {
            1: { label: 'File', ...CHART_SUBHEADER, sortable: true },
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            const chartData = info.row.original;
            const cellValue = info.getValue();
            const isReceived = chartData.status === 'completed';
            const isTruncated = cellValue?.length > 13;
            const truncatedValue = isTruncated
            ? `${cellValue.slice(0, 10)}...`
            : cellValue;
            if(isBatch){
                return (
                    <Tooltip
                    hasArrow
                    backgroundColor={!isReceived ? AppColors.secondary : undefined}
                    placement="bottom"
                    label={
                        isReceived
                        ? (isTruncated ? cellValue : '') // show only if truncated
                        : (
                            <>
                                Batch pending review.
                                {isTruncated && (
                                <>
                                    <br />
                                    {cellValue}
                                </>
                                )}
                            </>
                            )
                    }
                    >
                    <Text
                        whiteSpace="nowrap"
                        _hover={isReceived ? { textDecoration: 'underline' } : {}}
                        cursor={'pointer'}
                        fontWeight={600}
                        color="blue.800"
                        fontSize="14px"
                        textAlign="start"
                    >
                        {truncatedValue}
                    </Text>
                    </Tooltip>
                );
            }else{
              return <Text fontWeight={500} textAlign="start" color="blue.700" fontSize="13px">{info.getValue()}</Text>;
            }
          },
        }),

        // ── Col 2: Uploader Account ──────────────────────
        columnHelper.accessor('uploader', {
          id: 'location',
          header: () => 'Account',
          subHeader: {
            1: { label: 'Hospital', ...CHART_SUBHEADER, sortable: true },
            // sortable omitted → no sort icon on this sub-header column
          },
          sortValue: row => row.hospital?.name ?? '',
          cell: info => {
            const isBatch = info.row.depth === 0;
            const rowData = info.row.getParentRow()?.original;
            if (isBatch) {
              let uploaderDetails = '';
              if ((info.getValue()?.type !== ClientType.PHYSICIANS_GROUP || !info.row.original.hospital) && info.getValue()?.health_system) {
                uploaderDetails = `${info.getValue()?.health_system?.name} -`;
              }
              if (info.row.original.hospital) {
                uploaderDetails = `${uploaderDetails} ${info.row.original.hospital?.name} - ${info.row.original.hospital?.state_abbr} -`
              } else if (info.row.original.uploader?.health_system?.state_abbr) {
                uploaderDetails = `${uploaderDetails} ${info.row.original.uploader?.health_system?.state_abbr} -`
              }
              uploaderDetails = `${uploaderDetails} (${info.getValue()?.full_name})`
              return (
                <AttachmentCell
                  attachments={[uploaderDetails]} defaultMsg={"Assign"}/>
                )    
            } else {
              return <Text textAlign="center" fontSize="13px">{rowData?.hospital?.name} - {rowData?.hospital?.state_abbr}</Text>
            }
          },
        }),
    
        // ── Col 2: Upload Date (batch) / Attachments (chart) ──────────────────────
        columnHelper.accessor('upload_date', {
          id: 'upload_date',
          header: () => 'Upload Date',
          subHeader: {
            1: { label: 'Attachments', ...CHART_SUBHEADER },
            // sortable omitted → no sort icon on this sub-header column
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            const attachmentNames = [
              info.row.original.file_obj?.name,
              info.row.original.file_obj?.audited_chart_name,
              ...(info.row.original.attachments?.map(a => a.file.name) ?? [])
            ].filter(Boolean);
            if (isBatch) {
              const val = info.getValue();
              return val
                ? <Text textAlign="center" fontSize="14px" whiteSpace={"nowrap"}>{format(new Date(val), 'MM/dd/yyyy')}</Text>
                : <Text color="gray.400" fontSize="14px">N/A</Text>;
            }
            return <AttachmentCell attachments={attachmentNames} />;
          },
        }),
       
    
        // ── Col 3: Status ─────────────────────────────────────────────────────────
        columnHelper.accessor('status', {
          id: 'status',
          header: () => 'Status',
          subHeader: {
            1: { label: 'Status', ...CHART_SUBHEADER, sortable: true },
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            if (!isBatch) {
            }
            
            return isBatch
              ? <BatchStatusIndicator subRows={info.row.original.subRows} />
              : <ChartStatusBadge status={info.getValue()} />;
          },
        }),
    
        // ── Col 4: Specialty (chart) ─────────────────────
        columnHelper.accessor(row => row, {
          id: 'specialties',
          header: () => 'Specialties',
          subHeader: {
            // Different label at depth 1 — "No. of Charts" becomes "Specialty"
            1: { label: 'Specialty', ...CHART_SUBHEADER, sortable: true },
          },
          // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
          // accessorFn returns the whole row object which is not directly sortable
          sortValue: row => row.specialty?.name ?? '',
          cell: info => {
            const row     = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              
              return (
              //  <ClickableCell 
              //  onClick={() => {
              //     setTabId(0);
              //     ioState.on();
              //     setModalRowId(info.row.index);
              //   }}
              //   >
                  <AttachmentCell
                   attachments={[...new Set((row.subRows || []).map(chart => chart.specialty?.name).filter(val => !!val))]} />
                // </ClickableCell>     
              )
            }
            if (!row.specialty)
              return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
            return row.specialty?.name
              ? <Text textAlign="center" fontSize="13px">{row.specialty.name}</Text>
              : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
          },
        }),

            // ── Col 5: Audtiors ─────────────────────
        columnHelper.accessor(row => row, {
          id: 'auditors',
          header: () => 'Auditors',
          subHeader: {
            // Different label at depth 1 — "No. of Charts" becomes "Specialty"
            1: { label: 'Auditor', ...CHART_SUBHEADER, sortable: true },
          },
          // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
          // accessorFn returns the whole row object which is not directly sortable
          sortValue: row => row.auditor?.full_name ?? '',
          cell: info => {
            const row     = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              
              return (
                <AttachmentCell 
                attachments={[...new Set((row.subRows || []).map(chart => chart.auditor?.full_name).filter(val => !!val))]}
                />
              )
            }

            if (!row.auditor)
              return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
            return row.auditor?.full_name
              ? <Text textAlign="center" fontSize="13px">{row.auditor.full_name}</Text>
              : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
          },
        }),
    


         // ── Col 6: QA ─────────────────────────────────────────────────
        ...(authContext.currentUser?.role === 'ADMIN' ? [columnHelper.accessor(row => row, {
          id: 'qas',
          header: () => 'QA',
          subHeader: {
            // Different label at depth 1 — "No. of Charts" becomes "Specialty"
            1: { label: 'QA', ...CHART_SUBHEADER, sortable: true },
          },
          // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
          // accessorFn returns the whole row object which is not directly sortable
          sortValue: row => row.qa?.full_name ?? '',
          cell: info => {
            const row     = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              
              return (
                  <AttachmentCell 
                  attachments={[...new Set((row.subRows || []).map(chart => chart.qa?.full_name).filter(val => !!val))]} 
                  defaultMsg={"N/A"}/>
              )
          
            }
            if (!row.qa)
              return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
            return row.qa?.full_name
              ? <Text textAlign="center" fontSize="13px">{row.qa.full_name}</Text>
              : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
          },
        })] : []),
    
    
        // ── Col 7: Providers ────────────────────────────────────────────────────
        columnHelper.accessor(row => row, {
          id: 'providers',
          header: () => 'Providers',
          enableSorting: false,
          subHeader: {
            1: { label: 'Providers', ...CHART_SUBHEADER },
          },
          cell: info => {
            const rowVal = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              return <AttachmentCell attachments={rowVal.batch_providers} />
            }
            return <AttachmentCell attachments={(rowVal.providers ?? []).map(chart_prov => chart_prov.name)} />
          }
        }),
    
        // ── Col 8: Action — no sub-header label needed ────────────────────────────
        columnHelper.accessor(row => row.upload_id, {
          id: 'action',
          header: () => '',
          enableSorting: false,
          // subHeader intentionally omitted → blank cell in sub-header row
          cell: info => {
            const cellValue = info.getValue();
            const isBatch = info.row.depth === 0;
            const status = info.row.original.status;
            let readOnly = false;
            let isDisabled = false;
            if (!isBatch) {
              readOnly = READ_ONLY_SHEET_STATUSES.includes(status) || info.row.original?.qa?.id !== authContext.currentUser?.id;
              isDisabled = ["AWAITING AUDIT", "AWAITING ASSIGNMENT", "IN PROGRESS"].includes(status);
            } 
            const batchId = info.getValue();
            
            return (
              <HStack justifyContent="start" spacing={2}>
                <Tooltip
                  placement="top"
                  hasArrow
                  label={ isDisabled ? "Chart currently in Audit" : ""}
                >
                  <Box>
                    <PrimaryButton
                      bg={AppColors.secondary}
                      fontSize="14px"
                      fontWeight="normal"
                      size="sm"
                      borderRadius="3xl"
                      px={5}
                      isDisabled={isDisabled}
                      _hover={{
                        transition: 'all .1s ease',
                        bgColor: AppColors.white,
                        color: AppColors.secondary,
                        outline: `2px solid ${AppColors.secondary}`
                      }}
                      onClick={() => {
                        // retaining ordering and search text
                        const paginationSession = getPaginationSession(PAGE_NAME, TABLE_NAME);                        
                        setPaginationSession(PAGE_NAME, TABLE_NAME, {
                          ...paginationSession, 
                          ...('searchText' in filterParams && {search: filterParams.searchText}),
                          ...(filterParams.ordering && {ordering: filterParams.ordering}),
                          'assignedToMe': myAuditsCheckbox
                        })    
                        if (isBatch) {
                          onBatchNavigation(
                            info.row.index,
                            info.row.original.upload_id
                          )
                          navigate(
                            ROUTES.VIEW_BATCH_AUDIT_SHEET.replace(':batchId', cellValue)
                          );
                        } else {
                          onChartNavigation(
                            info.row.getParentRow().index,
                            info.row.index,
                            info.row.getParentRow().original.upload_id,
                            info.row.original.id
                          )
                          const routeFormat = { uploadId: info.row.original.id };
                          const route = readOnly ? ROUTES.VIEW_AUDIT_SHEET : ROUTES.AUDIT_SHEET;
                          navigate(replaceRoute(route, routeFormat));
                        }
                      }}
                    >
                      {isBatch ? 'View Batch' : (readOnly ? 'View File' : (status === "AWAITING REVIEW") ? "Start Review" : 'Review File')}
                    </PrimaryButton>
                  </Box>
                </Tooltip>
    
                {!isBatch ? (
                  <AuditSheetFileViewer
                    auditData={info.row.original}
                    icon={<EyeAvatarIcon 
                      style={{ cursor: 'pointer'}} 
                      onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                      onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}/>}
                    chart_id={info.row.original.id}   
                    onOpen={() => {
                    onChartNavigation(
                        info.row.getParentRow().index,
                        info.row.index,
                        info.row.getParentRow().original.upload_id,
                        info.row.original.id
                      )
                    }}
                    onClose={initializingNavState}
  
                  />
                ) : (
                <NotesDrawer
                tabType={"ALL"}
                batchId={batchId}
                onOpenNotes={obj => onOpenNotes(obj, batchId, "ALL")}
                style={{
                    width: 35,
                    height: 35,
                    cursor: 'pointer',
                    display: 'inline-block',
                    transition: "0.3s",
                    filter: "invert(1)"
                  }}
                  onOpen={() => {
                    onBatchNavigation(
                      info.row.index,
                      info.row.original.upload_id
                    )
                  }}
                  onClose={initializingNavState}                  
                  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                /> 
                )}
              </HStack>
            );
          }
        }),
    
      ], [isAllChartsLoading]);

    function handlePreviousPage() {
        getAllCharts.loadPrevious(PAGE_NAME, TABLE_NAME);
    }

    function handleNextPage() {
        getAllCharts.loadNext(PAGE_NAME, TABLE_NAME);
    }

    function handleSizeChange(size) {
        getAllCharts.loadSize(PAGE_NAME, TABLE_NAME, size );
    }

    function handlePageChangeByNumber(pageNumber) {
        getAllCharts.loadPageByNumber(PAGE_NAME, TABLE_NAME, pageNumber);
    }

    return (
        <Box>
            <CQTreeTable
                ref={tableRef}
                data={allCharts}
                columns={columns}
                getSubRows={(row) => row.subRows}
                autoExpand={row => row.expand}
                manualPagination={true}
                manualSortBy={false}
                isLoading={isAllChartsLoading}
                {...createTreeTableProps(highlightedRowId)}
                {...paginationProps(getAllCharts)}
                onAsc={header => {
                setOrdering(header);
                }}
                onDesc={header => {
                setOrdering(`-${header}`);
                }}
                onSortReset={() => {
                  setOrdering(undefined);
                }}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
                onSizeChange={handleSizeChange}
                onPageChangeByNumber={handlePageChangeByNumber}
            />
        </Box>
    )
}

export default NewAuditTable;